import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import domToImage from 'dom-to-image-more';
import React from 'react';
import ReactDOM from 'react-dom/client';
import UVFloorPlanCanvas from '../components/UVFloorPlanCanvas';
import { elevationWallGroups } from './elevationFreestandingWalls';
import {
  ROOM_PLAN_MEASUREMENT_BUFFER_M,
  roomPlanBounds,
} from './floorPlanRoomBounds';
import RenderWallElevation from '../components/elevations/RenderWallElevation';
import { waitForSVGsToLoad } from './svgLoadPoller';
import { captureOffscreenCanvas } from './offscreenCanvasCapture';
import { drawWalls, drawObjects } from './elementDrawingUtils';
import {
  getRemovedMeasurementsForRoom,
  getMeasurementOffsetsForRoom,
} from '../services/structureService';
import {
  DEBUG_SCALE_RULERS,
  FEET_PER_METER,
  MM_PER_INCH,
  pixelsPerFootForScale,
  pixelsPerMeterForScale,
  selectLargestFittingScale,
  selectCommonFittingScale,
  sizeImageAtScale,
  sizeImageToExactScaleMm,
  drawScaleRulerMm,
  drawDebugEdgeRulers,
} from './pdfScaleUtils';
import { toJpegDataUrl, trimImageWhitespace, loadImage } from './pdfImageUtils';
import { hydrateRoomOpenings } from './openingSyncUtils';
import { objectsNearWall } from './elevationWallObjects';
import { endOnWallBands } from './elevationEndOnWalls';
import {
  MAX_TIERS,
  TIERED_CAPTURE_PADDING_PX,
  tierDepthPx,
} from './elevationDimensionTiers';
import { roomDisplayName } from '@shared-lib/rooms/roomDisplayName';
import { roomCabinetTrimPieces } from './cabinetTrimElevation';

/** Pixel size of a data URL image. */
const imagePixelSize = async (src) => {
  const img = await loadImage(src);
  return { width: img.width, height: img.height };
};

/**
 * Crop a floor-plan capture to its ink and report the LOGICAL size of the
 * crop. Logical px per foot are unchanged by cropping, so every placer's
 * scale math stays exact while the fit can pick a larger preferred scale
 * (the margins no longer have to fit the cell). Falls back to the full
 * capture as JPEG when there is nothing to trim.
 */
const cropFloorPlanCapture = async (dataUrl, logicalWidth, logicalHeight) => {
  const trimmed = await trimImageWhitespace(dataUrl);
  if (!trimmed) {
    return {
      imageData: await toJpegDataUrl(dataUrl),
      imageWidth: logicalWidth,
      imageHeight: logicalHeight,
    };
  }
  return {
    imageData: trimmed.dataUrl,
    imageWidth: logicalWidth * trimmed.fraction.x,
    imageHeight: logicalHeight * trimmed.fraction.y,
  };
};

// Constants for 24" x 36" architectural sheet (in inches)
const SHEET_WIDTH_IN = 36;
const SHEET_HEIGHT_IN = 24;

// Convert inches to PDF points (72 points per inch)
const SHEET_WIDTH_PT = SHEET_WIDTH_IN * 72;
const SHEET_HEIGHT_PT = SHEET_HEIGHT_IN * 72;

// Margins and title block dimensions (in points)
const MARGIN = 36; // 0.5 inch margin
const TITLE_BLOCK_WIDTH = 360; // 5 inches wide
const TITLE_BLOCK_HEIGHT = 216; // 3 inches tall

/**
 * Generate a construction document PDF with standard architectural template
 * @param {Object} projectData - Project information
 * @param {string} projectData.name - Project name
 * @param {string} projectData.address - Project address
 * @param {string} projectData.description - Project description
 * @param {Date} projectData.date - Date for the document
 * @param {string} projectData.projectNumber - Project number/ID
 * @returns {jsPDF} PDF document instance
 */
export function generateConstructionDocument(projectData) {
  // Create a new PDF document in landscape orientation for 24" x 36"
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: [SHEET_WIDTH_PT, SHEET_HEIGHT_PT],
  });

  // Draw border
  drawBorder(pdf);

  // Draw title block
  drawTitleBlock(pdf, projectData);

  // Draw grid reference markers
  drawGridMarkers(pdf);

  // Add drawing area placeholder
  drawDrawingArea(pdf);

  return pdf;
}

/**
 * Draw the border of the sheet
 */
function drawBorder(pdf) {
  // Outer border
  pdf.setLineWidth(2);
  pdf.rect(
    MARGIN,
    MARGIN,
    SHEET_WIDTH_PT - 2 * MARGIN,
    SHEET_HEIGHT_PT - 2 * MARGIN
  );

  // Inner border (slightly inset)
  pdf.setLineWidth(0.5);
  pdf.rect(
    MARGIN + 18,
    MARGIN + 18,
    SHEET_WIDTH_PT - 2 * MARGIN - 36,
    SHEET_HEIGHT_PT - 2 * MARGIN - 36
  );
}

/**
 * Draw the title block in the bottom right corner
 */
function drawTitleBlock(pdf, projectData) {
  const titleBlockX = SHEET_WIDTH_PT - MARGIN - TITLE_BLOCK_WIDTH;
  const titleBlockY = SHEET_HEIGHT_PT - MARGIN - TITLE_BLOCK_HEIGHT;

  // Title block border
  pdf.setLineWidth(1);
  pdf.rect(titleBlockX, titleBlockY, TITLE_BLOCK_WIDTH, TITLE_BLOCK_HEIGHT);

  // Horizontal dividers
  const sections = [
    { height: 72, label: 'PROJECT' }, // 1 inch
    { height: 54, label: 'LOCATION' }, // 0.75 inch
    { height: 54, label: 'DRAWING TITLE' }, // 0.75 inch
    { height: 36, label: 'SCALE / DATE / DRAWN BY' }, // 0.5 inch
  ];

  let currentY = titleBlockY;
  sections.forEach((section, index) => {
    if (index > 0) {
      pdf.line(
        titleBlockX,
        currentY,
        titleBlockX + TITLE_BLOCK_WIDTH,
        currentY
      );
    }

    // Add section labels
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text(section.label, titleBlockX + 6, currentY + 12);

    // Add content based on project data
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');

    switch (index) {
      case 0: // Project name
        const projectName = projectData.name || 'PROJECT NAME';
        pdf.text(projectName.toUpperCase(), titleBlockX + 6, currentY + 36);
        break;
      case 1: // Location
        if (projectData.address) {
          pdf.setFontSize(10);
          const addressText = formatAddress(projectData.address);
          pdf.text(addressText, titleBlockX + 6, currentY + 30);
        }
        break;
      case 2: // Drawing title
        pdf.text('CONSTRUCTION DOCUMENTS', titleBlockX + 6, currentY + 36);
        break;
      case 3: // Scale, Date, Drawn by
        pdf.setFontSize(10);
        const dateStr = formatDate(projectData.date || new Date());
        pdf.text(`SCALE: AS NOTED`, titleBlockX + 6, currentY + 24);
        pdf.text(`DATE: ${dateStr}`, titleBlockX + 120, currentY + 24);
        pdf.text(`SHEET: A1.0`, titleBlockX + 240, currentY + 24);
        break;
    }

    currentY += section.height;
  });

  // Add project number if available
  if (projectData.projectNumber) {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(
      `PROJECT NO: ${projectData.projectNumber}`,
      titleBlockX + 240,
      titleBlockY + 12
    );
  }
}

/**
 * Draw grid reference markers along the edges
 */
function drawGridMarkers(pdf) {
  const gridSpacing = 432; // 6 inches
  const markerSize = 24;

  // Calculate number of vertical and horizontal divisions
  const drawingWidth = SHEET_WIDTH_PT - 2 * MARGIN - TITLE_BLOCK_WIDTH - 36;
  const drawingHeight = SHEET_HEIGHT_PT - 2 * MARGIN - 36;

  const numVertical = Math.floor(drawingWidth / gridSpacing);
  const numHorizontal = Math.floor(drawingHeight / gridSpacing);

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');

  // Top and bottom markers (numbers)
  for (let i = 0; i <= numVertical; i++) {
    const x = MARGIN + 36 + i * gridSpacing;

    // Top marker
    pdf.circle(x, MARGIN + 18, markerSize / 2);
    pdf.text(String(i + 1), x - 3, MARGIN + 22);

    // Bottom marker (avoid title block area)
    if (x < SHEET_WIDTH_PT - MARGIN - TITLE_BLOCK_WIDTH) {
      pdf.circle(x, SHEET_HEIGHT_PT - MARGIN - 18, markerSize / 2);
      pdf.text(String(i + 1), x - 3, SHEET_HEIGHT_PT - MARGIN - 14);
    }
  }

  // Left and right markers (letters)
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  for (let i = 0; i <= numHorizontal; i++) {
    const y = MARGIN + 36 + i * gridSpacing;

    // Left marker
    pdf.circle(MARGIN + 18, y, markerSize / 2);
    pdf.text(letters[i] || '', MARGIN + 15, y + 3);

    // Right marker (avoid title block area)
    if (y < SHEET_HEIGHT_PT - MARGIN - TITLE_BLOCK_HEIGHT) {
      pdf.circle(SHEET_WIDTH_PT - MARGIN - 18, y, markerSize / 2);
      pdf.text(letters[i] || '', SHEET_WIDTH_PT - MARGIN - 21, y + 3);
    }
  }
}

/**
 * Draw the main drawing area with placeholder text
 */
function drawDrawingArea(pdf) {
  const drawingX = MARGIN + 54;
  const drawingY = MARGIN + 54;
  const drawingWidth = SHEET_WIDTH_PT - 2 * MARGIN - TITLE_BLOCK_WIDTH - 72;
  const drawingHeight = SHEET_HEIGHT_PT - 2 * MARGIN - 72;

  // Dashed rectangle for drawing area
  pdf.setLineDash([3, 3]);
  pdf.setLineWidth(0.5);
  pdf.rect(drawingX, drawingY, drawingWidth, drawingHeight);
  pdf.setLineDash([]);

  // Add placeholder text
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(200, 200, 200);
  const text = 'DRAWING AREA';
  const textWidth = pdf.getTextWidth(text);
  pdf.text(
    text,
    drawingX + (drawingWidth - textWidth) / 2,
    drawingY + drawingHeight / 2
  );

  // Add instructions
  pdf.setFontSize(14);
  const instructions = 'Floor plans, elevations, and details will appear here';
  const instructionsWidth = pdf.getTextWidth(instructions);
  pdf.text(
    instructions,
    drawingX + (drawingWidth - instructionsWidth) / 2,
    drawingY + drawingHeight / 2 + 30
  );

  // Reset text color
  pdf.setTextColor(0, 0, 0);
}

/**
 * Format address for display in title block
 */
function formatAddress(address) {
  if (typeof address === 'string') {
    return address.toUpperCase();
  }

  if (typeof address === 'object') {
    const parts = [];
    if (address.streetAddress) parts.push(address.streetAddress);
    if (address.city && address.state) {
      parts.push(`${address.city}, ${address.state}`);
    } else if (address.city) {
      parts.push(address.city);
    } else if (address.state) {
      parts.push(address.state);
    }
    if (address.zipCode) parts.push(address.zipCode);

    return parts.join(' ').toUpperCase();
  }

  return '';
}

/**
 * Format date for display
 */
function formatDate(date) {
  const d = date instanceof Date ? date : new Date(date);
  const months = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
  ];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/**
 * Download the PDF document
 */
export function downloadPDF(pdf, filename) {
  pdf.save(filename);
}

/**
 * Generate comprehensive construction documents PDF with all tabs
 */
/**
 * Helper to calculate nearbyObjects for a wall
 * Uses EXACT logic from ElevationsViewer including dimension checks and bounds filtering
 */
function calculateNearbyObjects(checkWall, room) {
  const wallId = checkWall.id;
  const allDoors = room.doors || [];
  const allWindows = room.windows || [];

  // Same association rule as the on-screen ElevationsViewer.
  const nearbyObjects = objectsNearWall(checkWall, room.objects || []);

  // Add doors that match this wall
  allDoors.forEach((door) => {
    if (door.parentWallIds?.[0] === wallId) {
      // Use prealignment-center if center is not available
      let centerToUse = door.center;
      if (!centerToUse && door['prealignment-center']) {
        centerToUse = door['prealignment-center'];
      }
      nearbyObjects.push({
        ...door,
        center: centerToUse,
        distanceFromWall: 0,
      });
    }
  });

  // Add windows that match this wall
  allWindows.forEach((window) => {
    if (window.parentWallIds?.[0] === wallId) {
      // Use prealignment-center if center is not available
      let centerToUse = window.center;
      if (!centerToUse && window['prealignment-center']) {
        centerToUse = window['prealignment-center'];
      }
      nearbyObjects.push({
        ...window,
        center: centerToUse,
        distanceFromWall: 0,
      });
    }
  });

  return nearbyObjects;
}

/**
 * Helper to render elevation via RenderWallElevation React component and return as image data URL.
 * Uses the same component as the interactive web viewer for pixel-identical output.
 *
 * `structureOpenings` (optional): `{doors, windows, openings}` from the
 * structure-level arrays. Pass it whenever `room.doors / .windows /
 * .openings` may carry the post-PR #634 slim shape — the function
 * hydrates the room internally so calculateNearbyObjects can read
 * `door.center / .dimensions` off each iterated opening.
 *
 * `options.showMeasurements` (default true) draws the dimension lines. The
 * construction documents keep them; a customer proposal turns them off — a
 * homeowner reads an elevation as a picture of the wall, not a build sheet.
 */
export async function renderElevationToImage(
  wallGroup,
  room,
  glbObjects = {},
  objectProjections = null,
  wallNumberOffset = 0,
  architecturalScale = null, // Scale object with inchesPerFoot property
  removedMeasurements = {},
  measurementOffsets = {},
  measurementPadding = null, // Override padding in px (default: 260)
  structureOpenings = null,
  options = {}
) {
  const { showMeasurements = true } = options;
  try {
    const effectiveRoom = structureOpenings
      ? hydrateRoomOpenings(room, structureOpenings)
      : room;
    // Calculate nearby objects for this wall (or all walls if grouped).
    // The walls carry their end-on bands from here on — the on-screen viewer
    // resolves them in `useElevationsData`, and a printed elevation must show
    // the same returning walls. Mapped ONCE, because `associatedWall` below
    // is matched by IDENTITY against the segments the renderer builds: a
    // second copy would strand every object on the first segment.
    const withBands = (w) => ({
      ...w,
      endOnBands: w.endOnBands || endOnWallBands(w, effectiveRoom),
    });
    const wallsToCheck = (
      wallGroup.isGrouped ? wallGroup.groupedWalls : [wallGroup]
    ).map(withBands);
    const allNearbyObjects = [];
    const addedObjectIds = new Set();

    wallsToCheck.forEach((checkWall, wallIndex) => {
      const wallObjects = calculateNearbyObjects(checkWall, effectiveRoom);

      wallObjects.forEach((obj) => {
        const objId = obj.id;
        if (!addedObjectIds.has(objId)) {
          addedObjectIds.add(objId);
          allNearbyObjects.push({
            ...obj,
            associatedWallIndex: wallIndex,
            associatedWall: checkWall,
          });
        }
      });
    });

    // Create wall data object
    const wallData = {
      ...wallGroup,
      ...(wallGroup.isGrouped
        ? { groupedWalls: wallsToCheck }
        : { endOnBands: wallsToCheck[0].endOnBands }),
      nearbyObjects: allNearbyObjects,
      roomName: roomDisplayName(room.name),
      // the room with its windows filled in, for the valance's window check
      cabinetTrimPieces: roomCabinetTrimPieces(effectiveRoom),
    };

    // Render scale in logical px/m. The placement code re-derives the printed
    // size from the returned pixelsPerFoot, so this only sets resolution.
    const scalePixelsPerMeter = architecturalScale?.inchesPerFoot
      ? pixelsPerMeterForScale(architecturalScale)
      : null;

    // Get the wall ID for measurement lookups
    const wallId = wallData.id || wallGroup.id;

    // Create offscreen container with padding for measurements that extend
    // beyond the wall bounds (dimension lines, labels, extension lines).
    // How far the dimensions reach past the drawing. A capture prints the
    // TIERED set, whose depth is known (at most 3 lines a side), so this is
    // computed rather than the old fixed 260 px — which was a guess at "5
    // stacked lines" and silently CLIPPED anything beyond it.
    const maxMeasurementDepth = measurementPadding ?? TIERED_CAPTURE_PADDING_PX;
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.backgroundColor = '#ffffff';
    container.style.padding = `${maxMeasurementDepth}px`;
    document.body.appendChild(container);

    // Mount RenderWallElevation
    const root = ReactDOM.createRoot(container);
    root.render(
      React.createElement(RenderWallElevation, {
        wallData: wallData,
        index: wallNumberOffset,
        isPrimary: true,
        isPreview: false,
        previewScale: scalePixelsPerMeter,
        zoomScale: 1,
        showMeasurements,
        // Print is always tiered — the drawing set, never every chain.
        measurementFilter: 'tiered',
        removedMeasurements: wallId ? removedMeasurements[wallId] || [] : [],
        savedMeasurementOffsets: wallId ? measurementOffsets[wallId] || {} : {},
        objectProjections: objectProjections,
        glbObjects: glbObjects,
        primaryWallIndex: 0,
        selectedWallGroup: null,
        selectedObject: null,
        selectedObjectId: null,
        highlightedObjectIds: [],
        onObjectSelect: null,
        setSelectedObject: null,
        setSelectedObjectData: null,
        setSelectedMeasurementData: null,
        onMeasurementOffsetSave: null,
        isPanning: false,
      })
    );

    // Wait for SVG projections to load
    await waitForSVGsToLoad(container);

    // Capture at 2x resolution so thin measurement lines survive printing.
    // dom-to-image ignores devicePixelRatio, so captured px = logical px ×
    // CAPTURE_SCALE exactly; the returned imageWidth/Height are divided back
    // to logical px so placement is independent of this multiplier.
    const CAPTURE_SCALE = 2;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;
    const dataUrl = await domToImage.toPng(container, {
      bgcolor: '#ffffff',
      width: containerWidth * CAPTURE_SCALE,
      height: containerHeight * CAPTURE_SCALE,
      style: {
        transform: `scale(${CAPTURE_SCALE})`,
        transformOrigin: 'top left',
      },
    });

    // Cleanup
    root.unmount();
    document.body.removeChild(container);

    // Trim the SIDES only. Keeping the full height means every elevation of
    // the same frame comes back the same height, with its floor line a
    // constant `measurementPadding` above the bottom edge — which is what lets
    // a sheet stand them all on one ground line. Trimming vertically moves
    // that line by however deep each drawing's dimension rows are.
    const trimmed = await trimImageWhitespace(dataUrl, { axis: 'x' });
    const out = trimmed ?? {
      dataUrl: await toJpegDataUrl(dataUrl),
      ...(await imagePixelSize(dataUrl)),
    };

    // Same contract as renderFloorPlanToImage: logical px + logical px/ft.
    return {
      imageData: out.dataUrl,
      imageWidth: out.width / CAPTURE_SCALE,
      imageHeight: out.height / CAPTURE_SCALE,
      pixelsPerFoot: architecturalScale?.inchesPerFoot
        ? pixelsPerFootForScale(architecturalScale)
        : null,
    };
  } catch (error) {
    console.error('Error rendering elevation:', error);
    return null;
  }
}

/**
 * Render minimap-style floor plan (simple, with optional fixtures, with highlighting)
 * @param {Object} structure - Structure data
 * @param {Array} rooms - Array of rooms
 * @param {number} selectedRoomIndex - Index of room to highlight
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {boolean} showFixtures - Whether to show bathroom fixtures
 */
function renderMinimapFloorPlan(
  structure,
  rooms,
  selectedRoomIndex,
  width,
  height,
  showFixtures = false,
  parcelBoundaries = null
) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  // Fill background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  // Calculate bounds - include parcel boundaries if provided
  let minX = Infinity,
    maxX = -Infinity;
  let minZ = Infinity,
    maxZ = -Infinity;

  rooms.forEach((room) => {
    if (room.floors?.[0]?.polygonCorners) {
      room.floors[0].polygonCorners.forEach((corner) => {
        const x = corner[0] || corner.x || 0;
        const z = corner[1] || corner.z || 0;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minZ = Math.min(minZ, z);
        maxZ = Math.max(maxZ, z);
      });
    }
  });

  // Convert parcel boundaries to meters and include in bounds so we can see the whole parcel
  let convertedParcelBounds = null;
  if (
    parcelBoundaries &&
    Array.isArray(parcelBoundaries) &&
    parcelBoundaries.length > 0
  ) {
    const refLon = parcelBoundaries[0][0];
    const refLat = parcelBoundaries[0][1];
    const metersPerDegreeLat = 111320;
    const metersPerDegreeLon = 111320 * Math.cos((refLat * Math.PI) / 180);

    convertedParcelBounds = parcelBoundaries.map((coord) => {
      const lon = coord[0];
      const lat = coord[1];
      const x = (lon - refLon) * metersPerDegreeLon;
      const z = (lat - refLat) * metersPerDegreeLat;
      return [x, z];
    });

    // Include parcel in bounds
    convertedParcelBounds.forEach((coord) => {
      minX = Math.min(minX, coord[0]);
      maxX = Math.max(maxX, coord[0]);
      minZ = Math.min(minZ, coord[1]);
      maxZ = Math.max(maxZ, coord[1]);
    });
  }

  const structureWidth = maxX - minX;
  const structureHeight = maxZ - minZ;
  const padding = 40;
  const scale = Math.min(
    (width - padding) / structureWidth,
    (height - padding) / structureHeight
  );
  const offsetX = (width - structureWidth * scale) / 2;
  const offsetY = (height - structureHeight * scale) / 2;

  // Set up coordinate transformation for wall rendering
  // drawWalls expects world coordinates with origin at structure center
  const structureCenterX = (minX + maxX) / 2;
  const structureCenterZ = (minZ + maxZ) / 2;

  ctx.save();
  // Translate to canvas center
  ctx.translate(width / 2, height / 2);
  // Scale
  ctx.scale(scale, scale);
  // Translate so structure center is at origin
  ctx.translate(-structureCenterX, -structureCenterZ);

  // Draw parcel boundaries if provided (use pre-converted bounds)
  if (convertedParcelBounds && convertedParcelBounds.length > 0) {
    console.log(
      '[renderMinimapFloorPlan] ===== DRAWING PARCEL BOUNDARIES ====='
    );
    console.log(
      '[renderMinimapFloorPlan] Converted parcel bounds:',
      convertedParcelBounds
    );

    ctx.save();
    ctx.strokeStyle = '#FF0000'; // Red color for parcel boundaries
    ctx.lineWidth = 0.5 / scale; // Thicker line that's visible
    ctx.setLineDash([1.0 / scale, 0.5 / scale]); // Dashed line
    ctx.beginPath();

    convertedParcelBounds.forEach((corner, i) => {
      const x = corner[0];
      const z = corner[1];

      if (i === 0) {
        ctx.moveTo(x, z);
      } else {
        ctx.lineTo(x, z);
      }
    });

    // Close the boundary polygon
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
    console.log('[renderMinimapFloorPlan] ===== PARCEL BOUNDARIES DRAWN =====');
  }

  // Draw walls if available
  if (
    structure?.walls &&
    Array.isArray(structure.walls) &&
    structure.walls.length > 0
  ) {
    drawWalls(
      ctx,
      structure.walls,
      scale,
      null, // no selected item
      false, // not debug mode
      rooms,
      [], // windows
      [], // doors
      [], // openings
      {} // wallExtensionMetadata
    );
  }

  // Draw fixtures if requested
  if (showFixtures) {
    console.log(
      '[renderMinimapFloorPlan] showFixtures is true, processing rooms:',
      rooms.length
    );

    // Collect all objects from all rooms
    const allObjects = [];
    rooms.forEach((room) => {
      console.log(
        '[renderMinimapFloorPlan] Room:',
        room.name,
        'has objects:',
        room.objects?.length || 0
      );
      if (room.objects && Array.isArray(room.objects)) {
        room.objects.forEach((obj) => {
          allObjects.push(obj);
        });
      }
    });

    console.log(
      '[renderMinimapFloorPlan] Total objects collected:',
      allObjects.length
    );

    // Log first object to see structure
    if (allObjects.length > 0) {
      console.log('[renderMinimapFloorPlan] Sample object:', allObjects[0]);
      console.log(
        '[renderMinimapFloorPlan] Sample object category:',
        allObjects[0].category
      );
      console.log(
        '[renderMinimapFloorPlan] Sample object type:',
        allObjects[0].type
      );
    }

    // Filter for bathroom fixtures only
    const fixtures = allObjects.filter((obj) => {
      // Category is an object with the type as a key, e.g., {sink: {...}} or {toilet: {...}}
      if (obj.category) {
        const categoryKeys = Object.keys(obj.category);
        const type =
          categoryKeys.length > 0 ? categoryKeys[0].toLowerCase() : '';
        const isFixture =
          type === 'sink' || type === 'toilet' || type === 'bathtub';
        if (isFixture) {
          console.log('[renderMinimapFloorPlan] Found fixture:', type, obj);
        }
        return isFixture;
      }
      return false;
    });

    console.log(
      '[renderMinimapFloorPlan] Total fixtures to draw:',
      fixtures.length
    );

    if (fixtures.length > 0) {
      drawObjects(
        ctx,
        fixtures,
        { showFixtures: true }, // data parameter
        scale,
        null, // no selected item
        false // not debug mode
      );
    }
  }

  // Restore context for room drawing
  ctx.restore();

  // Draw each room
  console.log('[renderMinimapFloorPlan] ===== DRAWING ROOMS =====');
  console.log('[renderMinimapFloorPlan] Total rooms to draw:', rooms.length);
  console.log(
    '[renderMinimapFloorPlan] Selected room index:',
    selectedRoomIndex
  );

  rooms.forEach((room, roomIdx) => {
    console.log(
      `[renderMinimapFloorPlan] Processing room ${roomIdx}:`,
      room.name
    );
    console.log(
      `[renderMinimapFloorPlan] Room has floors:`,
      room.floors?.length || 0
    );
    console.log(
      `[renderMinimapFloorPlan] Room polygonCorners:`,
      room.floors?.[0]?.polygonCorners?.length || 0
    );

    if (room.floors?.[0]?.polygonCorners) {
      const corners = room.floors[0].polygonCorners;
      const isSelected = roomIdx === selectedRoomIndex;

      ctx.save();
      ctx.beginPath();

      corners.forEach((corner, index) => {
        const cornerX = corner[0] || corner.x || 0;
        const cornerZ = corner[1] || corner.z || 0;
        const x = (cornerX - minX) * scale + offsetX;
        const y = (cornerZ - minZ) * scale + offsetY;

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.closePath();

      // Fill and stroke
      if (isSelected) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.3)'; // Blue highlight
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 3;
      } else {
        ctx.fillStyle = 'rgba(200, 200, 200, 0.3)'; // Light gray
        ctx.strokeStyle = '#999999';
        ctx.lineWidth = 1;
      }

      ctx.fill();
      ctx.stroke();

      // Add room label - use room.name first, then check sections for label,
      // each the way a person writes it ("livingRoom" → "Living room")
      const labelText =
        roomDisplayName(room.name) ??
        roomDisplayName(
          (room.sections || []).find((section) => section.label)?.label
        );

      // Only draw label if we have text
      if (labelText) {
        // Calculate centroid (geometric center) of polygon using shoelace formula
        // This gives the true visual center, not just the average of vertices
        let areaSum = 0;
        let centroidX = 0;
        let centroidZ = 0;

        for (let i = 0; i < corners.length; i++) {
          const j = (i + 1) % corners.length;
          const xi = corners[i][0] || corners[i].x || 0;
          const zi = corners[i][1] || corners[i].z || 0;
          const xj = corners[j][0] || corners[j].x || 0;
          const zj = corners[j][1] || corners[j].z || 0;

          const cross = xi * zj - xj * zi;
          areaSum += cross;
          centroidX += (xi + xj) * cross;
          centroidZ += (zi + zj) * cross;
        }

        areaSum /= 2;
        const centerX = centroidX / (6 * areaSum);
        const centerZ = centroidZ / (6 * areaSum);

        // Transform to canvas coordinates (same as polygon vertices)
        const textX = (centerX - minX) * scale + offsetX;
        const textY = (centerZ - minZ) * scale + offsetY;

        // Use larger font size for better visibility in PDFs
        const fontSize = isSelected ? 24 : 20;
        ctx.fillStyle = isSelected ? '#1e40af' : '#000000';
        ctx.font = isSelected
          ? `bold ${fontSize}px sans-serif`
          : `${fontSize}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        console.log(
          `[renderMinimapFloorPlan] Room ${roomIdx} center in world coords: (${centerX.toFixed(2)}, ${centerZ.toFixed(2)})`
        );
        console.log(
          `[renderMinimapFloorPlan] Drawing label for "${labelText}" at canvas coords (${textX.toFixed(1)}, ${textY.toFixed(1)})`
        );
        console.log(
          `[renderMinimapFloorPlan] Font: ${ctx.font}, Color: ${ctx.fillStyle}`
        );

        ctx.fillText(labelText, textX, textY);
      } else {
        console.log(
          `[renderMinimapFloorPlan] Room ${roomIdx} has no name or section label, skipping`
        );
      }

      ctx.restore();
    } else {
      console.log(
        `[renderMinimapFloorPlan] Room ${roomIdx} has no floor/polygonCorners, skipping`
      );
    }
  });

  console.log('[renderMinimapFloorPlan] ===== FINISHED DRAWING ROOMS =====');

  return canvas.toDataURL('image/png');
}

/**
 * Helper to render floor plan offscreen and capture as image
 * @param {Object} structure - Structure data
 * @param {Array} rooms - Array of rooms
 * @param {number} selectedRoomIndex - Index of selected room (-1 for all rooms)
 * @param {boolean} showAllRooms - Whether to show all rooms
 * @param {number} targetWidth - Target width in pixels for the canvas (default 800)
 * @param {number} targetHeight - Target height in pixels for the canvas (default 600)
 * @returns {Object} { imageData: string, pixelsPerFoot: number, imageWidth: number, imageHeight: number }
 */
export async function renderFloorPlanToImage(
  structure,
  rooms,
  selectedRoomIndex,
  showAllRooms = false,
  targetWidth = 800,
  targetHeight = 600,
  showFixtures = false,
  parcelBoundaries = null,
  architecturalScale = null, // Scale object with inchesPerFoot property
  // Canvas props the full (non-minimap) render passes straight through:
  //   structureWalls  structure-level walls — the thick grey walls the in-app
  //                   2D view draws; without them only zero-thickness room
  //                   walls appear
  //   selectedStory   which floor of a stacked structure to draw (null = all)
  options = {}
) {
  // Use minimap renderer for full structure view
  if (showAllRooms) {
    const cropped = await cropFloorPlanCapture(
      renderMinimapFloorPlan(
        structure,
        rooms,
        selectedRoomIndex,
        targetWidth,
        targetHeight,
        showFixtures,
        parcelBoundaries
      ),
      targetWidth,
      targetHeight
    );
    return { ...cropped, pixelsPerFoot: null };
  }
  return new Promise((resolve) => {
    // Prepare floor plan data
    // If showAllRooms is true, show all rooms with optional highlight
    // If selectedRoomIndex is -1, show all rooms with no highlight
    // Otherwise, show only the selected room
    const displayAllRooms = showAllRooms || selectedRoomIndex === -1;
    const roomsToShow = displayAllRooms ? rooms : [rooms[selectedRoomIndex]];

    // IMPORTANT: UVFloorPlanCanvas behavior:
    // - When showAllRooms=true AND selectedRoom is set: shows all rooms and HIGHLIGHTS the selected one
    // - When showAllRooms=false AND selectedRoom is set: shows only that room
    // - When selectedRoom is null: shows all rooms with no highlight
    const selectedRoom =
      selectedRoomIndex >= 0 && selectedRoomIndex < rooms.length
        ? rooms[selectedRoomIndex]
        : null;

    // Forward canonical structure-level openings so UVFloorPlanCanvas can
    // hydrate the slim room copies it iterates. Without these, the canvas's
    // structureOpeningLookup is empty and doors / windows render at (0, 0)
    // with undefined dimensions. `doors / windows / openings` are the keys
    // the canvas's lookup reads; `structureDoors / structureWindows /
    // structureOpenings` are preserved for symmetry with the loaded
    // structure shape.
    const floorPlanData = {
      id: structure.id,
      name: structure.name,
      projectId: structure.projectId,
      rooms: roomsToShow,
      // Include structure-level walls when showing all rooms for better visualization
      walls: displayAllRooms ? structure.walls : undefined,
      structureDoors: structure.structureDoors,
      structureWindows: structure.structureWindows,
      structureOpenings: structure.structureOpenings,
      doors: structure.structureDoors,
      windows: structure.structureWindows,
      openings: structure.structureOpenings,
    };

    // Calculate optimal scale to fit the displayed rooms. Same bounds the
    // callers use to CHOOSE a scale — see floorPlanRoomBounds.
    const bounds = roomPlanBounds(roomsToShow, {
      // A single room draws its walls' measurements; the all-rooms minimap
      // does not, so its bounds stay on the floor plates.
      includeWalls: !displayAllRooms,
      bufferM: displayAllRooms ? 0 : ROOM_PLAN_MEASUREMENT_BUFFER_M,
    });

    const structureWidth = bounds?.widthM ?? NaN;
    const structureHeight = bounds?.heightM ?? NaN;
    const padding = displayAllRooms ? 150 : 120; // More padding for full structure

    // Calculate scale to fit - ensure it fits within bounds
    let fitScale = 70; // Default scale
    if (architecturalScale && architecturalScale.inchesPerFoot) {
      // Calculate scale from architectural scale
      // architecturalScale.inchesPerFoot = inches on paper per foot in real life
      // We need pixels per meter
      // 1 meter = 3.28084 feet
      // Target canvas size represents the PDF page size at some DPI
      // For jsPDF, 1 inch on paper ≈ targetWidth / (available inches)

      // We need to determine how many pixels per inch on our canvas
      // Let's assume targetWidth represents the available width in inches * pixelsPerInch
      // For a typical PDF at 96 DPI: 1 inch = 96 pixels
      // But since we're using this for jsPDF which works at 72 DPI equivalent,
      // we'll use a conversion where the canvas pixels map directly to PDF space

      fitScale = pixelsPerMeterForScale(architecturalScale);

      console.log(
        `[renderFloorPlanToImage] Using architectural scale: ${architecturalScale.description}`
      );
      console.log(
        `[renderFloorPlanToImage] Calculated fitScale: ${fitScale} pixels/meter`
      );
    }

    // If no architectural scale provided, calculate fitScale to fit the content
    if (
      !architecturalScale &&
      isFinite(structureWidth) &&
      isFinite(structureHeight) &&
      structureWidth > 0 &&
      structureHeight > 0
    ) {
      const scaleX = (targetWidth - padding) / structureWidth;
      const scaleY = (targetHeight - padding) / structureHeight;
      // Use the smaller scale to ensure everything fits
      // Cap at 500 px/m for very small rooms (0.5cm per pixel is excellent detail)
      // Min at 20 px/m for very large structures (5cm per pixel is acceptable)
      fitScale = Math.min(scaleX, scaleY, 500);
      fitScale = Math.max(fitScale, 20);
    }

    // Calculate pixelsPerFoot from fitScale (which is logical pixels per meter)
    const pixelsPerFoot = fitScale / FEET_PER_METER;

    captureOffscreenCanvas(
      React.createElement(UVFloorPlanCanvas, {
        data: floorPlanData,
        // A drawing, not the app: the red collision outlines and hatching are
        // an in-app warning and have no meaning on a printed sheet. The
        // section export already turns them off.
        showCollisionIndicator: false,
        selectedRoom: showAllRooms ? null : selectedRoom, // null when showing all rooms
        externalShowRoomLabels: true,
        showMeasurements: showAllRooms ? false : true, // No measurements on minimap
        externalMeasurementFilter: 'room',
        showFixtures: showAllRooms ? false : true, // No fixtures on minimap (full structure view)
        showFurnishings: showAllRooms ? false : true, // No furnishings on minimap (full structure view)
        // Staging follows furnishings here: the minimap is a locator, and
        // dressing has no business on one.
        showStaging: showAllRooms ? false : true,
        initialScale: fitScale,
        structureWalls: options.structureWalls ?? [],
        selectedStory: options.selectedStory ?? null,
        // Highlight the selected room when showing all rooms
        highlightedElements:
          showAllRooms && selectedRoom
            ? { type: 'room', elements: [selectedRoom] }
            : { type: null, elements: [] },
      }),
      { width: targetWidth, height: targetHeight }
    ).then(async (png) => {
      // Crop to ink + JPEG (see cropFloorPlanCapture); logical px/ft unchanged.
      const cropped = png
        ? await cropFloorPlanCapture(png, targetWidth, targetHeight)
        : {
            imageData: null,
            imageWidth: targetWidth,
            imageHeight: targetHeight,
          };

      // Return object with image data and scale info for accurate scale calculation at placement time
      resolve({ ...cropped, pixelsPerFoot });
    });
  });
}

export async function generateConstructionDocsPDF(
  structure,
  rooms,
  docData,
  glbObjects = {},
  objectProjections = null
) {
  if (!structure) {
    throw new Error('Structure data is required');
  }

  if (!docData) {
    throw new Error('Document data is required');
  }

  const pdf = new jsPDF('p', 'mm', 'letter');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;

  // ===== COVER PAGE =====
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Construction Documents', pageWidth / 2, 40, { align: 'center' });

  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'normal');
  pdf.text(structure.name || 'Building Structure', pageWidth / 2, 60, {
    align: 'center',
  });

  pdf.setFontSize(12);
  pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 80, {
    align: 'center',
  });

  let yPos = 100;
  pdf.setFontSize(10);
  pdf.text(`Total Rooms: ${docData.metadata?.totalRooms || 0}`, margin, yPos);
  yPos += 7;
  pdf.text(`Total Doors: ${docData.metadata?.totalDoors || 0}`, margin, yPos);
  yPos += 7;
  pdf.text(
    `Total Windows: ${docData.metadata?.totalWindows || 0}`,
    margin,
    yPos
  );

  // ===== DOOR SCHEDULE =====
  pdf.addPage();
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Door Schedule', margin, 20);

  if (docData.schedules?.doors && docData.schedules.doors.length > 0) {
    autoTable(pdf, {
      startY: 30,
      head: [['#', 'Room', 'Width', 'Height', 'Type', 'Remarks']],
      body: docData.schedules.doors.map((door) => [
        door.number,
        door.room,
        `${door.widthFeet}'`,
        `${door.heightFeet}'`,
        door.type,
        door.remarks || '-',
      ]),
      theme: 'grid',
      headStyles: { fillColor: [66, 139, 202] },
      margin: { left: margin, right: margin },
    });
  }

  // ===== WINDOW SCHEDULE =====
  pdf.addPage();
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Window Schedule', margin, 20);

  if (docData.schedules?.windows && docData.schedules.windows.length > 0) {
    autoTable(pdf, {
      startY: 30,
      head: [['#', 'Room', 'Width', 'Height', 'Sill Height', 'Remarks']],
      body: docData.schedules.windows.map((window) => [
        window.number,
        window.room,
        `${window.widthFeet}'`,
        `${window.heightFeet}'`,
        window.sillHeight ? `${window.sillHeight}'` : '-',
        window.remarks || '-',
      ]),
      theme: 'grid',
      headStyles: { fillColor: [66, 139, 202] },
      margin: { left: margin, right: margin },
    });
  }

  // ===== ROOM SCHEDULE =====
  pdf.addPage();
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Room Schedule', margin, 20);

  if (docData.schedules?.rooms && docData.schedules.rooms.length > 0) {
    autoTable(pdf, {
      startY: 30,
      head: [['Room', 'Area (sq ft)', 'Width', 'Depth', 'Doors', 'Windows']],
      body: docData.schedules.rooms.map((room) => [
        roomDisplayName(room.name) ?? '',
        room.areaSquareFeet,
        `${room.widthFeet}'`,
        `${room.depthFeet}'`,
        room.doorCount,
        room.windowCount,
      ]),
      theme: 'grid',
      headStyles: { fillColor: [66, 139, 202] },
      margin: { left: margin, right: margin },
    });
  }

  // ===== ROOM PAGES =====
  // For each room, create a page with: full floor plan (highlighted), room floor plan, and elevations
  for (let roomIdx = 0; roomIdx < (rooms?.length || 0); roomIdx++) {
    const room = rooms[roomIdx];

    // Skip if no walls
    const walls = room.walls || [];
    if (walls.length === 0) continue;

    pdf.addPage();

    // Title
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(roomDisplayName(room.name) ?? `Room ${roomIdx + 1}`, margin, 20);

    let currentY = 30;

    // Calculate dynamic sizing based on number of elevations
    const groupedWallsTemp = elevationWallGroups(walls, room);
    const numElevations = groupedWallsTemp.length;

    // Allocate space: more elevations = less space for floor plans
    const totalPageHeight = pageHeight - margin - 30; // Available height after title
    let floorPlanHeight;

    if (numElevations <= 2) {
      // Few elevations, give more space to floor plans
      floorPlanHeight = totalPageHeight * 0.5;
    } else if (numElevations <= 4) {
      // Medium elevations
      floorPlanHeight = totalPageHeight * 0.4;
    } else {
      // Many elevations, minimize floor plan space
      floorPlanHeight = totalPageHeight * 0.3;
    }

    // ===== 1. SMALL FULL FLOOR PLAN IN TOP RIGHT =====
    let fullPlanWidth = 0;
    let fullPlanHeight = 0;
    try {
      // Use fixed high-res square canvas - let PDF scaling handle aspect ratio
      const fullFloorPlanResult = await renderFloorPlanToImage(
        structure,
        rooms,
        roomIdx,
        true,
        1600, // Fixed width
        1600 // Fixed height (square for flexibility)
      );
      const fullFloorPlanData = fullFloorPlanResult?.imageData ?? null;

      if (fullFloorPlanData) {
        const img = new Image();
        img.src = fullFloorPlanData;
        await new Promise((resolve) => {
          img.onload = resolve;
        });

        const imgWidth = img.width;
        const imgHeight = img.height;
        const aspectRatio = imgWidth / imgHeight;

        // Full plan gets 25% of page width, up to 90% of allocated floor plan height
        const maxWidth = (pageWidth - 2 * margin) * 0.25;
        const maxHeight = floorPlanHeight * 0.9;

        // Fit image to available space maintaining aspect ratio
        let width = maxWidth;
        let height = width / aspectRatio;

        if (height > maxHeight) {
          height = maxHeight;
          width = height * aspectRatio;
        }

        fullPlanWidth = width;
        fullPlanHeight = height;

        // Position in top right
        const x = pageWidth - margin - width;
        const y = currentY;

        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Full Plan', x, currentY - 2);

        pdf.addImage(fullFloorPlanData, 'JPEG', x, y, width, height);
      }
    } catch (error) {
      console.error(
        `Error capturing full floor plan for room ${roomIdx}:`,
        error
      );
    }

    // ===== 2. INDIVIDUAL ROOM FLOOR PLAN (LEFT SIDE) =====
    try {
      const roomFloorPlanResult = await renderFloorPlanToImage(
        structure,
        rooms,
        roomIdx,
        false,
        2000,
        2000
      );

      if (roomFloorPlanResult?.imageData) {
        // Use remaining width (left of full plan) and allocated height
        const maxWidthMm = pageWidth - 2 * margin - fullPlanWidth - 10;
        const maxHeightMm = floorPlanHeight;

        // Size image to match an exact architectural scale
        const {
          width,
          height,
          scale: exactScale,
        } = sizeImageToExactScaleMm(
          roomFloorPlanResult.pixelsPerFoot,
          roomFloorPlanResult.imageWidth,
          roomFloorPlanResult.imageHeight,
          maxWidthMm,
          maxHeightMm
        );
        const x = margin;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Room Floor Plan', margin, currentY);
        currentY += 5;

        pdf.addImage(
          roomFloorPlanResult.imageData,
          'JPEG',
          x,
          currentY,
          width,
          height
        );

        // Draw scale ruler
        drawScaleRulerMm(pdf, exactScale, x + width, currentY + height + 2);

        // Advance currentY by the maximum of room floor plan height or full plan height
        currentY += Math.max(height, fullPlanHeight) + 10;
      }
    } catch (error) {
      console.error(
        `Error capturing room floor plan for room ${roomIdx}:`,
        error
      );
      currentY += Math.max(0, fullPlanHeight) + 10;
    }

    // ===== 3. ROOM ELEVATIONS =====
    // Group walls using the same logic as ElevationsViewer
    const groupedWalls = elevationWallGroups(walls, room);

    if (groupedWalls.length > 0) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Wall Elevations', margin, currentY);
      currentY += 5;

      // Calculate max wall height for elevation scale
      let maxWallHeight = 3; // default 3m
      walls.forEach((wall) => {
        const yCoords = wall.corners?.map((c) => c.y) || [];
        if (yCoords.length > 0) {
          const h = Math.max(...yCoords) - Math.min(...yCoords);
          if (h > maxWallHeight) maxWallHeight = h;
        }
      });

      // Determine grid layout
      const availableWidth = pageWidth - 2 * margin;
      const availableHeight = pageHeight - currentY - margin;
      let cols, rows;
      if (groupedWalls.length === 1) {
        cols = 1;
        rows = 1;
      } else if (groupedWalls.length === 2) {
        cols = 2;
        rows = 1;
      } else if (groupedWalls.length <= 4) {
        cols = 2;
        rows = 2;
      } else if (groupedWalls.length <= 6) {
        cols = 3;
        rows = 2;
      } else {
        cols = 3;
        rows = 3;
      }

      const cellHeight = availableHeight / rows;
      const cellHeightInches = cellHeight / MM_PER_INCH;

      // Render scale: largest preferred scale that fits the wall height. This
      // only sets render resolution — the PLACED scale is chosen below from
      // the captured images (which also carry measurement padding).
      const maxWallHeightFeet = maxWallHeight * FEET_PER_METER;
      const renderScale = selectLargestFittingScale(
        0,
        maxWallHeightFeet,
        Infinity,
        cellHeightInches * 0.8
      );

      // Load measurement data for this room
      let removedMeasurements = {};
      let measurementOffsets = {};
      try {
        [removedMeasurements, measurementOffsets] = await Promise.all([
          getRemovedMeasurementsForRoom(room.id),
          getMeasurementOffsetsForRoom(room.id),
        ]);
      } catch (err) {
        console.warn('Could not load measurement data for room:', room.id, err);
      }

      // Capture all elevation groups with the calculated scale
      const elevationImages = [];
      for (let groupIdx = 0; groupIdx < groupedWalls.length; groupIdx++) {
        const wallGroup = groupedWalls[groupIdx];

        try {
          const result = await renderElevationToImage(
            wallGroup,
            room,
            glbObjects,
            objectProjections,
            0,
            renderScale,
            removedMeasurements,
            measurementOffsets,
            null,
            {
              doors: structure?.doors,
              windows: structure?.windows,
              openings: structure?.openings,
            }
          );

          if (result) {
            elevationImages.push({ ...result, groupIdx });
          }
        } catch (error) {
          console.error(
            `Error capturing elevation group ${groupIdx} for room ${roomIdx}:`,
            error
          );
        }
      }

      // Layout elevation groups in a grid
      if (elevationImages.length > 0) {
        const cellWidth = availableWidth / cols;
        const padding = 5;
        const maxW = cellWidth - 2 * padding;
        const maxH = cellHeight - 2 * padding - 15;

        // One placed scale for the whole sheet: the largest at which every
        // captured image fits its cell. The ruler below names THIS scale.
        const elevationScale = selectCommonFittingScale(
          elevationImages,
          maxW / MM_PER_INCH,
          maxH / MM_PER_INCH
        );

        const elevationLayouts = elevationImages.map((elevation, idx) => {
          const placed = sizeImageAtScale(
            elevation.pixelsPerFoot,
            elevation.imageWidth,
            elevation.imageHeight,
            elevationScale
          );
          return {
            ...elevation,
            scaledWidth: placed.width * MM_PER_INCH,
            scaledHeight: placed.height * MM_PER_INCH,
            col: idx % cols,
            row: Math.floor(idx / cols),
          };
        });

        // Find the maximum height for each row to align bottoms
        const rowMaxHeights = [];
        for (let r = 0; r < rows; r++) {
          const elevationsInRow = elevationLayouts.filter((el) => el.row === r);
          if (elevationsInRow.length > 0) {
            rowMaxHeights[r] = Math.max(
              ...elevationsInRow.map((el) => el.scaledHeight)
            );
          } else {
            rowMaxHeights[r] = 0;
          }
        }

        elevationLayouts.forEach((elevation) => {
          const { col, row } = elevation;
          const cellX = margin + col * cellWidth;
          const cellY = currentY + row * cellHeight;

          const bottomY = cellY + cellHeight - 15 - padding;
          const y = bottomY - elevation.scaledHeight;
          const x = cellX + (cellWidth - elevation.scaledWidth) / 2;

          pdf.addImage(
            elevation.imageData,
            'JPEG',
            x,
            y,
            elevation.scaledWidth,
            elevation.scaledHeight
          );

          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.text(
            `Elevation ${elevation.groupIdx + 1}`,
            cellX + cellWidth / 2,
            cellY + 10,
            { align: 'center' }
          );
        });

        // Draw elevation scale ruler at bottom right
        drawScaleRulerMm(
          pdf,
          elevationScale,
          pageWidth - margin,
          pageHeight - margin
        );
      }
    }
  }

  // Debug: draw 1" interval rulers on all pages
  if (DEBUG_SCALE_RULERS) {
    const totalPages = pdf.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      pdf.setPage(p);
      drawDebugEdgeRulers(pdf, pageWidth, pageHeight, MM_PER_INCH);
    }
  }

  // Save the PDF
  const filename = `${structure.name || 'structure'}_construction_docs_${new Date().getTime()}.pdf`;

  try {
    pdf.save(filename);
  } catch (saveError) {
    console.error('Error saving PDF:', saveError);
    throw new Error(`Failed to save PDF: ${saveError.message}`);
  }
}
